<template>
  <main class="home">
    <WCProductRowModern
      v-if="showOnSaleRecommendations"
      class="my-md-3 wc-product-recommendations-rows"
      :rowId="'OSR-9999-9999'"
      :rowName="t('osrRowTitle')"
      :isPersonalized="true"
      :hideIfEmpty="true"
      :entryMethod="entryMethodOnSale"
      data-testid="onSaleRecommendationsRow"
    />
    <WCProductRowModern
      v-if="showHomepageRecommendations"
      class="my-md-3 wc-product-recommendations-rows"
      :rowId="'HPR-9999-9999'"
      :rowName="t('hprRowTitle')"
      :isPersonalized="true"
      :hideIfEmpty="true"
      :entryMethod="entryMethodHomePage"
      data-testid="homepageRecommendationsRow"
    />
    <div :class="{ 'wc-coupon-recommendations-rows-desktop': !isMobile }">
      <WCCouponRowModern
        v-if="showCouponRecommendations"
        class="my-md-3"
        :useRecs="true"
        :isPersonalized="true"
        @noRecs="overrideCouponRecommendations = true"
        data-testid="homepageCouponRecommendationsRow"
      />
    </div>
    <WCConfiguredProductSectionRows
      :name="$configuration.store.name"
      :rows="$configuration.rows"
      :hideCoupons="showCouponRecommendations"
    />
  </main>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { configurationKey } from '@/injections';
import WCProductRowModern from '@/components/WCProductRow/WCProductRowModern.vue';
import WCCouponRowModern from '@/components/WCCouponRow/WCCouponRowModern.vue';
import WCConfiguredProductSectionRows from '@/views/Home/components/WCConfiguredProductSectionRows/WCConfiguredProductSectionRows.vue';
import { ENTRY_METHOD } from '@/models/entities/cart/entry-method';
import usePlatform from '@/modules/platform/composables/usePlatform';

const store = useStore();
const { t } = useI18n();
const configuration = inject(configurationKey);
const { platform } = usePlatform();

const overrideCouponRecommendations = ref(false);

const isMobile = computed(() => platform.value.isMobile);

const isGuest = computed(() => store.getters['user/isGuest']);

const showHomepageRecommendations = computed(
  () => configuration?.homepageRecommendationsEnabled && !isGuest.value,
);

const showOnSaleRecommendations = computed(
  () => configuration?.onSaleRecommendationsEnabled && !isGuest.value,
);

const showCouponRecommendations = computed(
  () =>
    configuration?.couponRecommendationsEnabled &&
    configuration?.allowCouponClipping &&
    !overrideCouponRecommendations.value &&
    !isGuest.value,
);

const entryMethodOnSale = ENTRY_METHOD.RECOMMENDATION_ON_SALE;
const entryMethodHomePage = ENTRY_METHOD.RECOMMENDATION_HOME_PAGE;

const setWebAds = (ads: unknown[]) => store.dispatch('browse/setWebAds', ads);

onMounted(() => {
  setWebAds(configuration?.ads);
});

onUnmounted(() => {
  setWebAds([]);
});
</script>

<style lang="scss" scoped>
.wc-product-recommendations-rows {
  :deep(.product-row-class-desktop) {
    padding: 0 2rem !important;
  }
}
.wc-coupon-recommendations-rows-desktop {
  :deep(.item-row) {
    padding: 0 2rem !important;
  }
}

:deep([data-testid='onSaleRecommendationsRow'] h2.wc-product-row-modern__title),
:deep([data-testid='homepageRecommendationsRow'] h2.wc-product-row-modern__title),
:deep([data-testid='homepageCouponRecommendationsRow'] h2.wc-coupon-row-modern__title) {
  &::after {
    visibility: visible;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
